<template>
  <div class="title-management-page">
    <div class="settings-card">
      <h2 class="page-title">
        <Icon icon="heroicons:document-text" width="24" height="24" />
        标题管理
      </h2>

      <!-- 搜索区域 -->
      <div class="search-bar">
        <div class="search-left">
          <el-input v-model="searchForm.title_name" placeholder="请输入标题名称" clearable size="large" class="search-input"
            @keyup.enter="handleSearch">
            <template #prefix>
              <Icon icon="heroicons:magnifying-glass" width="18" height="18" />
            </template>
          </el-input>

          <el-button type="primary" size="large" @click="handleSearch" class="search-btn">
            <Icon icon="heroicons:magnifying-glass" width="18" height="18" />
            <span class="btn-text">搜索</span>
          </el-button>
        </div>

        <el-button type="success" size="large" @click="openAddDialog" class="add-btn">
          <Icon icon="heroicons:plus" width="18" height="18" />
          <span class="btn-text">新增标题</span>
        </el-button>
      </div>

      <!-- 表格包装器 -->
      <div class="table-wrapper">
        <el-table :data="titleList" stripe v-loading="loading" class="title-table"
          :row-style="{ background: 'transparent' }" :cell-style="{ background: 'transparent' }"
          :header-cell-style="{ background: 'rgba(157, 78, 221, 0.15)', color: '#fff' }">
          <el-table-column prop="title_name" label="标题名称" width="180" />
          <el-table-column prop="title_type" label="写作类型" width="120">
            <template #default="{ row }">
              <span class="type-tag" :class="row.title_type === 1 ? 'manual' : 'ai'">
                {{ row.title_type === 1 ? '手动导入' : 'AI生成' }}
              </span>
            </template>
          </el-table-column>

          <el-table-column prop="title_keyword" label="标题关键词" width="180">
            <template #default="{ row }">
              <span v-if="row.title_type === 2" class="info-text">{{ row.title_keyword }}</span>
              <span v-else class="empty-text">-</span>
            </template>
          </el-table-column>

          <el-table-column prop="title_limit" label="生成上限" width="120">
            <template #default="{ row }">
              <span v-if="row.title_type === 2" class="info-text">{{ row.title_limit }}</span>
              <span v-else class="empty-text">-</span>
            </template>
          </el-table-column>

          <el-table-column label="标题列表" width="150" fixed="right">
            <template #default="scope">
              <button class="action-btn view-btn" @click="openTitleListDialog(scope.row)">
                <Icon icon="heroicons:eye" width="14" height="14" />
                查看标题
              </button>
            </template>
          </el-table-column>

          <el-table-column label="AI生成" width="150" fixed="right">
            <template #default="scope">
              <button v-if="scope.row.title_type === 2" class="action-btn ai-btn"
                @click="openAICreateDialog(scope.row)">
                <Icon icon="heroicons:sparkles" width="14" height="14" />
                查看AI生成
              </button>
              <span v-else class="empty-text">-</span>
            </template>
          </el-table-column>

          <el-table-column prop="notes" label="备注" min-width="150" show-overflow-tooltip>
            <template #default="{ row }">
              <span class="notes-text">{{ row.notes || '-' }}</span>
            </template>
          </el-table-column>

          <el-table-column prop="create_time" label="创建时间" width="170" />
          <el-table-column prop="update_time" label="更新时间" width="170" />

          <el-table-column label="操作" width="200" fixed="right">
            <template #default="scope">
              <button class="action-btn edit-btn" @click="openEditDialog(scope.row)">
                <Icon icon="heroicons:pencil-square" width="14" height="14" />
                <span class="btn-text">编辑</span>
              </button>
              <button class="action-btn delete-btn" @click="handleDelete(scope.row.id)">
                <Icon icon="heroicons:trash" width="14" height="14" />
                <span class="btn-text">删除</span>
              </button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 分页组件 -->
      <div class="pagination-container">
        <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
          :total="pagination.total" :page-sizes="[10, 20, 30, 50]" :layout="paginationLayout"
          @size-change="fetchTitleList" @current-change="fetchTitleList" background />
      </div>
    </div>

    <!-- 新增/编辑对话框 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" :width="dialogWidth" :close-on-click-modal="false"
      class="custom-dialog" :top="dialogTop">
      <el-form ref="titleForm" :model="titleForm" :label-width="formLabelWidth" :rules="formRules">
        <el-form-item label="标题名称" prop="title_name">
          <el-input v-model="titleForm.title_name" placeholder="请输入标题名称" size="large" />
        </el-form-item>

        <el-form-item label="写作类型" prop="title_type">
          <div class="radio-group">
            <label class="radio-item" :class="{ active: titleForm.title_type === 1 }">
              <input type="radio" v-model="titleForm.title_type" :value="1" @change="handleTypeChange" />
              <span>手动导入</span>
            </label>
            <label class="radio-item" :class="{ active: titleForm.title_type === 2 }">
              <input type="radio" v-model="titleForm.title_type" :value="2" @change="handleTypeChange" />
              <span>AI生成</span>
            </label>
          </div>
        </el-form-item>

        <el-form-item label="是否润色" prop="polish_open" v-if="titleForm.title_type === 1">
          <div class="radio-group">
            <label class="radio-item" :class="{ active: titleForm.polish_open === 1 }">
              <input type="radio" v-model="titleForm.polish_open" :value="1" />
              <span>润色</span>
            </label>
            <label class="radio-item" :class="{ active: titleForm.polish_open === 0 }">
              <input type="radio" v-model="titleForm.polish_open" :value="0" />
              <span>不润色</span>
            </label>
          </div>
        </el-form-item>

        <el-form-item label="标题接口" prop="title_model_id" v-if="titleForm.title_type === 2">
          <el-select v-model="titleForm.title_model_id" placeholder="请选择标题接口" filterable clearable
            :loading="loadingModels">
            <el-option label="不选择" :value="-1" />
            <el-option v-for="model in modelList" :key="model.id" :label="model.writing_name" :value="model.id" />
          </el-select>
        </el-form-item>

        <el-form-item label="标题指令" prop="title_instruct_id" v-if="titleForm.title_type === 2">
          <el-select v-model="titleForm.title_instruct_id" placeholder="请选择标题指令" filterable clearable
            :loading="loadingInstructs">
            <el-option label="不选择" :value="-1" />
            <el-option v-for="instruct in instructList" :key="instruct.id" :label="instruct.instruct_name"
              :value="instruct.id" />
          </el-select>
        </el-form-item>

        <el-form-item label="标题关键词" prop="title_keyword" v-if="titleForm.title_type === 2">
          <el-input v-model="titleForm.title_keyword" type="textarea" :rows="3" placeholder="请输入标题关键词, 多个关键词用逗号分割！" />
          <div class="input-tip">
            <button class="tip-btn" type="button" @click="addSeparator">
              <Icon icon="heroicons:plus" width="14" height="14" />
              增加分隔符
            </button>
          </div>
        </el-form-item>

        <el-form-item label="生成上限" prop="title_limit" v-if="titleForm.title_type === 2">
          <el-input v-model="titleForm.title_limit" placeholder="请输入生成上限" />
        </el-form-item>

        <el-form-item label="备注信息">
          <el-input v-model="titleForm.notes" placeholder="请输入备注信息" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>

      <template #footer>
        <button class="dialog-btn cancel-btn" @click="dialogVisible = false">取消</button>
        <button class="dialog-btn confirm-btn" @click="submitTitleForm">确认</button>
      </template>
    </el-dialog>

    <!-- 查看标题对话框 -->
    <el-dialog v-model="titleListDialogVisible" :title="titleListDialogTitle" :width="childDialogWidth" top="5vh"
      :close-on-click-modal="false" :lock-scroll="false" :append-to-body="true"
      class="custom-dialog parent-view-dialog">
      <TitleAiCrateView v-if="titleListDialogVisible && currentTitleType === 2" :title-id="currentTitleId"
        :store-id="userInfo.id" />
      <TitleListView v-else-if="titleListDialogVisible" :title-id="currentTitleId" :store-id="userInfo.id" />
    </el-dialog>

    <!-- 查看AI生成对话框 -->
    <el-dialog v-model="aiCreateDialogVisible" :title="aiCreateDialogTitle" :width="childDialogWidth" top="5vh"
      :close-on-click-modal="false" :lock-scroll="false" :append-to-body="true"
      class="custom-dialog parent-view-dialog">
      <AICreateView v-if="aiCreateDialogVisible" :title-id="currentTitleId" :store-id="userInfo.id" />
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import { Icon } from '@iconify/vue';
import TitleListView from '@/components/TitleListView.vue'
import AICreateView from '@/components/AICreateView.vue'
import TitleAiCrateView from '@/components/TitleAiCrateView.vue'
import { ElMessage, ElMessageBox } from 'element-plus';

export default {
  name: 'SeoconfigTitle',
  components: { Icon, TitleListView, AICreateView, TitleAiCrateView },
  data() {
    return {
      loading: false,
      dialogVisible: false,
      dialogTitle: '新增标题',
      isEditMode: false,
      instructList: [],
      loadingInstructs: false,
      searchForm: { title_name: '' },
      titleForm: {
        id: null, title_type: 1, title_name: '', title_keyword: '', title_limit: '',
        notes: '', title_model_id: null, title_instruct_id: null, polish_open: 1
      },
      formRules: {
        title_name: [{ required: true, message: '请输入标题名称', trigger: 'blur' }],
        title_type: [{ required: true, message: '请选择写作类型', trigger: 'change' }]
      },
      titleList: [],
      modelList: [],
      loadingModels: false,
      titleListDialogVisible: false,
      aiCreateDialogVisible: false,
      titleListDialogTitle: '',
      aiCreateDialogTitle: '',
      currentTitleId: null,
      currentTitleType: 1,
      pagination: { page: 1, pageSize: 10, total: 0 },
      screenWidth: window.innerWidth
    }
  },
  computed: {
    userInfo() { return JSON.parse(Cookies.get('user_info') || '{}') },
    paginationLayout() {
      if (this.screenWidth < 576) return 'prev, pager, next'
      if (this.screenWidth < 768) return 'total, prev, pager, next'
      return 'total, sizes, prev, pager, next, jumper'
    },
    dialogWidth() {
      if (this.screenWidth < 576) return '95%'
      if (this.screenWidth < 768) return '90%'
      return '600px'
    },
    childDialogWidth() {
      if (this.screenWidth < 576) return '95%'
      if (this.screenWidth < 768) return '90%'
      return '1200px'
    },
    dialogTop() {
      if (this.screenWidth < 576) return '5vh'
      return '10vh'
    },
    formLabelWidth() {
      if (this.screenWidth < 576) return '80px'
      return '100px'
    }
  },
  mounted() {
    this.fetchTitleList();
    this.fetchModelList();
    this.fetchInstructList();
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    handleResize() { this.screenWidth = window.innerWidth },
    handleTypeChange() {
      const isAIType = this.titleForm.title_type === 2;
      if (isAIType) this.titleForm.polish_open = 1;
      this.formRules.title_model_id = [{ required: isAIType, message: '请选择标题接口', trigger: 'change' }];
      this.formRules.title_keyword = [{ required: isAIType, message: '请输入标题关键词', trigger: 'blur' }];
      this.formRules.title_instruct_id = [{ required: isAIType, message: '请选择标题指令', trigger: 'change' }];
      this.$nextTick(() => { if (this.$refs.titleForm) this.$refs.titleForm.clearValidate(['title_model_id', 'title_keyword', 'title_instruct_id']); });
    },
    addSeparator() {
      if (this.titleForm.title_keyword && !this.titleForm.title_keyword.trim().endsWith(',')) {
        this.titleForm.title_keyword += ',';
      } else if (!this.titleForm.title_keyword) {
        this.titleForm.title_keyword = ',';
      }
    },
    async fetchInstructList() {
      this.loadingInstructs = true;
      try {
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/CreationInstructionsController.php`, {
          action: 'list_all', store_id: this.userInfo.id
        });
        if (data.data) this.instructList = data.data.filter(instruct => instruct.instruct_type === 2);
      } catch (error) { ElMessage.error('获取标题指令失败'); } finally { this.loadingInstructs = false; }
    },
    async fetchModelList() {
      this.loadingModels = true;
      try {
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/WritingModelKeyController.php`, {
          action: 'list_all', store_id: this.userInfo.id
        });
        if (data.data) this.modelList = data.data.filter(model => model.writing_type === "2");
      } catch (error) { ElMessage.error('获取标题模型失败'); } finally { this.loadingModels = false; }
    },
    async fetchTitleList() {
      this.loading = true;
      try {
        const params = {
          action: this.searchForm.title_name ? 'search' : 'list',
          store_id: this.userInfo.id,
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        };
        if (this.searchForm.title_name) params.title_name = this.searchForm.title_name;
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/title_management.php`, params);
        if (data.data) { this.titleList = data.data; this.pagination.total = data.pagination?.total || data.data.length; }
      } catch (error) { ElMessage.error('获取标题列表失败'); } finally { this.loading = false; }
    },
    openTitleListDialog(row) {
      this.currentTitleId = row.id;
      this.currentTitleType = row.title_type;
      this.titleListDialogTitle = `${row.title_name} - 标题列表`;
      this.titleListDialogVisible = true;
    },
    openAICreateDialog(row) {
      this.currentTitleId = row.id;
      this.aiCreateDialogTitle = `${row.title_name} - AI生成记录`;
      this.aiCreateDialogVisible = true;
    },
    handleSearch() { this.pagination.page = 1; this.fetchTitleList(); },
    openAddDialog() {
      this.dialogTitle = '新增标题';
      this.isEditMode = false;
      this.titleForm = {
        id: null, title_type: 1, title_name: '', title_keyword: '', title_limit: '',
        notes: '', title_model_id: null, title_instruct_id: null, polish_open: 1
      };
      this.dialogVisible = true;
    },
    openEditDialog(row) {
      this.dialogTitle = '编辑标题';
      this.isEditMode = true;
      this.titleForm = {
        id: row.id, title_type: row.title_type, title_name: row.title_name,
        title_keyword: row.title_type === 2 ? row.title_keyword : '',
        title_limit: row.title_limit, notes: row.notes,
        title_model_id: row.title_type === 2 ? row.title_model_id : null,
        title_instruct_id: row.title_type === 2 ? row.title_instruct_id : null,
        polish_open: row.polish_open ?? 1
      };
      this.dialogVisible = true;
    },
    submitTitleForm() {
      this.handleTypeChange();
      this.$refs.titleForm.validate(async (valid) => {
        if (valid) {
          try {
            const action = this.isEditMode ? 'update' : 'add';
            const payload = { ...this.titleForm, store_id: this.userInfo.id };
            if (this.titleForm.title_type === 1) {
              payload.title_keyword = ''; payload.title_model_id = 0; payload.title_instruct_id = 0;
            }
            if (this.titleForm.title_type === 2) this.titleForm.polish_open = 1;
            const { data } = await axios.post(`${config.BASE_URL}/shop/api/title_management.php`, { action, ...payload });
            if (data.success) {
              ElMessage.success(this.isEditMode ? '更新成功' : '添加成功');
              this.dialogVisible = false;
              this.fetchTitleList();
            } else { ElMessage.error(data.error || '操作失败'); }
          } catch (error) { ElMessage.error('操作失败'); }
        }
      });
    },
    async handleDelete(id) {
      try {
        await ElMessageBox.confirm('确定要删除该标题配置吗？', '警告', {
          confirmButtonText: '确认删除', cancelButtonText: '取消', type: 'warning'
        });
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/title_management.php`, {
          action: 'delete', id, store_id: this.userInfo.id
        });
        if (data.success) { ElMessage.success('删除成功'); this.fetchTitleList(); }
        else { ElMessage.error(data.error || '删除失败'); }
      } catch (error) { if (error !== 'cancel') ElMessage.error('删除失败'); }
    }
  }
}
</script>

<style scoped lang="scss">
.title-management-page {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.settings-card {
  background: rgba(18, 14, 33, 0.6);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid rgba(157, 78, 221, 0.2);
  padding: 24px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 16px;
  }

  @media (max-width: 576px) {
    padding: 12px;
  }
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: #c084fc;
  }

  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 16px;
  }
}

.search-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;

  .search-left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    flex: 1;
  }

  .search-input {
    flex: 1;
    min-width: 200px;
  }

  @media (max-width: 768px) {
    flex-direction: column;

    .search-left {
      width: 100%;
    }

    .search-left .el-input {
      width: 100% !important;
    }

    .search-left .search-btn {
      width: 100%;
    }

    .add-btn {
      width: 100%;
    }
  }
}

.table-wrapper {
  position: relative;
  width: 100%;
  overflow-x: auto;
  margin-top: 20px;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(157, 78, 221, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(157, 78, 221, 0.4);
    border-radius: 4px;

    &:hover {
      background: rgba(157, 78, 221, 0.6);
    }
  }
}

:deep(.el-table) {
  --el-table-bg-color: transparent !important;
  --el-table-tr-bg-color: transparent !important;
  --el-table-header-bg-color: transparent !important;
  --el-table-row-hover-bg-color: rgba(157, 78, 221, 0.1) !important;
  --el-table-current-row-bg-color: rgba(157, 78, 221, 0.15) !important;
  --el-table-stripe-row-bg-color: rgba(157, 78, 221, 0.05) !important;
  --el-table-border-color: rgba(157, 78, 221, 0.2) !important;

  background-color: transparent !important;
  color: #e0e0e0;

  &::before {
    display: none;
  }

  .el-table__header-wrapper {
    background-color: transparent !important;

    tr {
      background-color: transparent !important;

      th:not(.el-table-fixed-column--right) {
        background: rgba(157, 78, 221, 0.2) !important;
        color: #fff !important;
        font-weight: 600;
        border-bottom: 1px solid rgba(157, 78, 221, 0.3) !important;

        .cell {
          color: #fff !important;
        }
      }
    }
  }

  .el-table__body-wrapper {
    background-color: transparent !important;

    tr {
      background-color: transparent !important;

      td:not(.el-table-fixed-column--right) {
        background-color: transparent !important;
        border-bottom: 1px solid rgba(157, 78, 221, 0.1);
        color: #e0e0e0;
      }

      &.el-table__row--striped td:not(.el-table-fixed-column--right) {
        background-color: rgba(157, 78, 221, 0.05) !important;
      }

      &:hover td:not(.el-table-fixed-column--right) {
        background-color: rgba(157, 78, 221, 0.1) !important;
      }
    }
  }

  .el-table-fixed-column--right {
    background-color: #120e21 !important;
    backdrop-filter: none !important;
  }

  th.el-table-fixed-column--right {
    background-color: #2a1f45 !important;
  }

  .el-table__row--striped .el-table-fixed-column--right {
    background-color: #1a1430 !important;
  }

  .el-table__empty-block {
    background-color: transparent !important;

    .el-table__empty-text {
      color: #a0a0c0 !important;
    }
  }
}

.type-tag {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;

  &.manual {
    background: rgba(68, 189, 50, 0.15);
    color: #4ade80;
  }

  &.ai {
    background: rgba(157, 78, 221, 0.15);
    color: #c084fc;
  }
}

.info-text {
  color: #4ade80;
  font-size: 13px;
}

.empty-text {
  color: #7a7a9a;
}

.notes-text {
  color: #a0a0c0;
  font-size: 13px;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  margin: 0 4px;

  &.view-btn {
    background: rgba(157, 78, 221, 0.15);
    border: 1px solid rgba(157, 78, 221, 0.4);
    color: #c084fc;

    &:hover {
      background: rgba(157, 78, 221, 0.25);
    }
  }

  &.ai-btn {
    background: rgba(245, 158, 11, 0.15);
    border: 1px solid rgba(245, 158, 11, 0.4);
    color: #fbbf24;

    &:hover {
      background: rgba(245, 158, 11, 0.25);
    }
  }

  &.edit-btn {
    background: rgba(157, 78, 221, 0.15);
    border: 1px solid rgba(157, 78, 221, 0.4);
    color: #c084fc;

    &:hover {
      background: rgba(157, 78, 221, 0.25);
    }
  }

  &.delete-btn {
    background: rgba(239, 68, 68, 0.15);
    border: 1px solid rgba(239, 68, 68, 0.4);
    color: #f87171;

    &:hover {
      background: rgba(239, 68, 68, 0.25);
    }
  }

  @media (max-width: 576px) {
    .btn-text {
      display: none;
    }
  }
}

.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;

  @media (max-width: 768px) {
    justify-content: center;
  }
}

:deep(.el-pagination) {
  --el-pagination-bg-color: transparent;
  --el-pagination-button-bg-color: rgba(157, 78, 221, 0.1);
  --el-pagination-hover-color: #c084fc;

  .btn-prev,
  .btn-next,
  .el-pager li {
    background: rgba(157, 78, 221, 0.1);
    border: 1px solid rgba(157, 78, 221, 0.3);
    color: #a0a0c0;
    border-radius: 8px;
    min-width: 32px;
    height: 32px;

    &:hover {
      background: rgba(157, 78, 221, 0.2);
      color: #c084fc;
    }

    &.active {
      background: linear-gradient(135deg, #6546b4, #9d4edd);
      color: #fff;
      border-color: transparent;
    }
  }

  .el-pagination__total,
  .el-pagination__jump {
    color: #a0a0c0;
  }

  @media (max-width: 576px) {

    .btn-prev,
    .btn-next,
    .el-pager li {
      min-width: 28px;
      height: 28px;
      margin: 0 2px;
    }
  }
}

:deep(.custom-dialog) {
  --el-dialog-bg-color: rgba(18, 14, 33, 0.98);
  --el-dialog-border-radius: 20px;

  .el-dialog__header {
    border-bottom: 1px solid rgba(157, 78, 221, 0.2);
    padding: 20px 24px;

    .el-dialog__title {
      color: #fff;
      font-weight: 600;
    }
  }

  .el-dialog__body {
    padding: 24px;
  }

  .el-dialog__footer {
    border-top: 1px solid rgba(157, 78, 221, 0.2);
    padding: 16px 24px;
  }

  @media (max-width: 576px) {
    .el-dialog__header {
      padding: 16px;
    }

    .el-dialog__body {
      padding: 16px;
    }

    .el-dialog__footer {
      padding: 12px 16px;
    }
  }
}

:deep(.el-form-item__label) {
  color: #a0a0c0;
  font-weight: 500;
}

:deep(.el-input__wrapper) {
  background: rgba(26, 21, 53, 0.8);
  border: 1px solid rgba(157, 78, 221, 0.3);
  border-radius: 10px;
  box-shadow: none;

  &:hover {
    border-color: rgba(157, 78, 221, 0.6);
  }

  &.is-focus {
    border-color: #9d4edd;
    box-shadow: 0 0 0 2px rgba(157, 78, 221, 0.2);
  }

  .el-input__inner {
    color: #fff;
    background: transparent;

    &::placeholder {
      color: #5a5a7a;
    }
  }
}

:deep(.el-textarea__inner) {
  background: rgba(26, 21, 53, 0.8);
  border: 1px solid rgba(157, 78, 221, 0.3);
  border-radius: 10px;
  color: #fff;

  &:hover {
    border-color: rgba(157, 78, 221, 0.6);
  }

  &:focus {
    border-color: #9d4edd;
    box-shadow: 0 0 0 2px rgba(157, 78, 221, 0.2);
  }
}

.radio-group {
  display: flex;
  gap: 20px;

  .radio-item {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    color: #a0a0c0;

    input {
      cursor: pointer;
      accent-color: #9d4edd;
    }

    &.active span {
      color: #c084fc;
    }
  }
}

.input-tip {
  margin-top: 8px;

  .tip-btn {
    background: rgba(157, 78, 221, 0.15);
    border: 1px solid rgba(157, 78, 221, 0.4);
    color: #c084fc;
    padding: 4px 12px;
    border-radius: 6px;
    font-size: 12px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 4px;

    &:hover {
      background: rgba(157, 78, 221, 0.25);
    }
  }
}

.dialog-btn {
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  margin-left: 12px;

  &.cancel-btn {
    background: rgba(157, 78, 221, 0.15);
    border: 1px solid rgba(157, 78, 221, 0.4);
    color: #a0a0c0;

    &:hover {
      background: rgba(157, 78, 221, 0.25);
      color: #c084fc;
    }
  }

  &.confirm-btn {
    background: linear-gradient(135deg, #6546b4, #9d4edd);
    color: #fff;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 15px rgba(157, 78, 221, 0.4);
    }
  }

  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 13px;
  }
}

:deep(.el-button) {
  border-radius: 10px;
  padding: 10px 20px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;

  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 13px;
  }
}

:deep(.el-button--primary) {
  background: linear-gradient(135deg, #6546b4, #9d4edd);
  border: none;

  &:hover {
    transform: translateY(-1px);
  }
}

:deep(.el-button--success) {
  background: linear-gradient(135deg, #10b981, #34d399);
  border: none;

  &:hover {
    transform: translateY(-1px);
  }
}

@media (max-width: 480px) {
  .btn-text {
    display: none;
  }
}
</style>

<!-- 非 scoped 全局样式，确保子弹窗完全脱离容器限制 -->
<style lang="scss">

/* 这行是关键：强制修改所有 el-dialog 的 CSS 变量默认值 */
.el-dialog {
  --el-dialog-bg-color: rgba(18, 14, 33, 0.98) !important;
}

/* 父弹窗 body 背景色 */
body>.el-overlay:has(.custom-dialog) .el-dialog__body {
  background: rgba(18, 14, 33, 0.98) !important;
}

/* 子组件内弹窗全局样式覆盖 */
.child-dialog {
  z-index: 3000 !important;
}

body>.el-overlay:has(.child-dialog) {
  z-index: 3000 !important;
}

body>.el-overlay:has(.child-dialog) .el-dialog {
  --el-dialog-bg-color: rgba(18, 14, 33, 0.98) !important;
  border: 1px solid rgba(157, 78, 221, 0.4) !important;
  border-radius: 20px;
  background: rgba(18, 14, 33, 0.98) !important;
}

body>.el-overlay:has(.child-dialog) .el-dialog__header {
  border-bottom: 1px solid rgba(157, 78, 221, 0.2);
  padding: 20px 24px;
  background: transparent !important;
}

body>.el-overlay:has(.child-dialog) .el-dialog__title {
  color: #ffffff !important;
  font-weight: 600;
}

body>.el-overlay:has(.child-dialog) .el-dialog__body {
  padding: 24px;
  color: #e0e0e0;
  background: rgba(18, 14, 33, 0.98) !important;
}

body>.el-overlay:has(.child-dialog) .el-dialog__footer {
  border-top: 1px solid rgba(157, 78, 221, 0.2);
  padding: 16px 24px;
  background: transparent !important;
}

body>.el-overlay:has(.child-dialog) .el-input__wrapper {
  background: rgba(26, 21, 53, 0.8) !important;
  border: 1px solid rgba(157, 78, 221, 0.3) !important;
  box-shadow: none !important;
  border-radius: 10px;
}

body>.el-overlay:has(.child-dialog) .el-input__wrapper:hover {
  border-color: rgba(157, 78, 221, 0.6) !important;
}

body>.el-overlay:has(.child-dialog) .el-input__wrapper.is-focus {
  border-color: #9d4edd !important;
  box-shadow: 0 0 0 2px rgba(157, 78, 221, 0.2) !important;
}

body>.el-overlay:has(.child-dialog) .el-input__inner {
  color: #fff !important;
  background: transparent !important;
}

body>.el-overlay:has(.child-dialog) .el-textarea__inner {
  background: rgba(26, 21, 53, 0.8) !important;
  border: 1px solid rgba(157, 78, 221, 0.3) !important;
  color: #fff !important;
  border-radius: 10px;
}

body>.el-overlay:has(.child-dialog) .el-upload-dragger {
  background: rgba(26, 21, 53, 0.6) !important;
  border-color: rgba(157, 78, 221, 0.4) !important;
}

body>.el-overlay:has(.child-dialog) .el-tabs__item {
  color: #a0a0c0 !important;
}

body>.el-overlay:has(.child-dialog) .el-tabs__item.is-active {
  color: #c084fc !important;
}

body>.el-overlay:has(.child-dialog) .el-tabs__active-bar {
  background-color: #9d4edd !important;
}

body>.el-overlay:has(.child-dialog) .el-form-item__label {
  color: #a0a0c0 !important;
}

body>.el-overlay:has(.child-dialog) .el-radio {
  color: #a0a0c0;
  --el-radio-input-border-color: rgba(157, 78, 221, 0.5);
}

body>.el-overlay:has(.child-dialog) .el-radio__input.is-checked .el-radio__inner {
  background-color: #9d4edd;
  border-color: #9d4edd;
}

body>.el-overlay:has(.child-dialog) .el-radio__input.is-checked+.el-radio__label {
  color: #c084fc;
}

body>.el-overlay:has(.child-dialog) .el-table {
  background-color: transparent !important;
  --el-table-bg-color: transparent !important;
  --el-table-tr-bg-color: transparent !important;
}

body>.el-overlay:has(.child-dialog) .el-table__body-wrapper {
  background-color: transparent !important;
}
</style>